import { useState } from "react"
import { Loader2, X } from "lucide-react"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { useGetAccountsQuery } from "@apis/accounts/useAccounts"
import { useDebounce } from "@hooks/useDebounce"
import type { ISearchItem } from "@commonTypes/searchResponse"

const MIN_QUERY_LENGTH = 5
const DEBOUNCE_MS = 400

type TAccountSearchSelectProps = {
  onSelect: (account: ISearchItem) => void
  onClear: () => void
  selected: ISearchItem | null
  excludeAccountId?: string
  label?: string
}

export function AccountSearchSelect({
  onSelect,
  onClear,
  selected,
  excludeAccountId,
  label = "Search for an account",
}: TAccountSearchSelectProps) {
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS)

  const { data, isLoading } = useGetAccountsQuery(
    { query: debouncedQuery, page: 1, per_page: 20 },
    debouncedQuery.length >= MIN_QUERY_LENGTH,
  )

  const accounts = (data?.accounts ?? []).filter(
    (account) => account.account_id !== excludeAccountId,
  )

  if (selected) {
    return (
      <div className="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-gray-200 tw:p-3">
        <div>
          <p className="tw:font-medium">{selected.name}</p>
          <p className="tw:text-sm tw:text-gray-500">
            {selected.service_address}
          </p>
          {selected.account_id && (
            <p className="tw:text-xs tw:text-gray-400">
              Account ID: {selected.account_id}
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          <X className="tw:h-4 tw:w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="tw:space-y-2">
      <label className="tw:text-sm tw:font-medium">{label}</label>
      <Input
        placeholder="Search by name, account #, or address..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {isLoading && (
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:text-sm tw:text-gray-500">
          <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
          Searching...
        </div>
      )}
      {!isLoading &&
        debouncedQuery.length >= MIN_QUERY_LENGTH &&
        accounts.length === 0 && (
          <p className="tw:py-2 tw:text-sm tw:text-gray-500">
            No accounts found
          </p>
        )}
      {accounts.length > 0 && (
        <ul className="tw:max-h-48 tw:overflow-y-auto tw:rounded-md tw:border tw:border-gray-200">
          {accounts.map((account) => (
            <li key={account.account_id ?? account.prospect_guid}>
              <button
                type="button"
                className="tw:w-full tw:px-3 tw:py-2 tw:text-left tw:hover:bg-gray-100"
                onClick={() => {
                  onSelect(account)
                  setQuery("")
                }}
              >
                <p className="tw:font-medium">{account.name}</p>
                <p className="tw:text-sm tw:text-gray-500">
                  {account.service_address}
                </p>
                {account.account_id && (
                  <p className="tw:text-xs tw:text-gray-400">
                    Account ID: {account.account_id}
                  </p>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
